import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faCompress,
  faDownload,
  faEye,
  faExpand,
  faFileExport,
  faFilePdf,
  faPaperclip,
  faSpinner,
  faSyncAlt,
  faTimes,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-hot-toast";
import { BASE_URL } from "../../config";

const APPROVAL_FILTERS = [
  { value: "FOR_REVIEW", label: "For Review" },
  { value: "PENDING", label: "Pending Final Approval" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "ALL", label: "All" },
];

const ENTRY_ACTION_TYPES = {
  MARK_VALID_RTP: {
    label: "Mark as Valid RTP",
    endpoint: `${BASE_URL}/claims/processing/rtp-entry/valid-request`,
    remarksRequired: true,
    attachmentsRequired: false,
    buttonClass: "bg-emerald-600 hover:bg-emerald-700",
    gradientClass: "from-emerald-600 to-emerald-500",
    remarksPlaceholder: "State why this claim should stay as For RTP (required)",
    submitLabel: "Submit Mark as Valid RTP",
  },
  MARK_NOT_VALID_RTP: {
    label: "Mark Not Valid RTP",
    endpoint: `${BASE_URL}/claims/processing/rtp-entry/not-valid-request`,
    remarksRequired: false,
    attachmentsRequired: false,
    buttonClass: "bg-rose-600 hover:bg-rose-700",
    gradientClass: "from-rose-600 to-rose-500",
    remarksPlaceholder: "Additional review remarks (optional)",
    submitLabel: "Submit Mark Not Valid",
  },
  RECONSIDER_OVERRIDE: {
    label: "Reconsider Override",
    endpoint: `${BASE_URL}/claims/processing/rtp-entry/reconsider-override-request`,
    remarksRequired: true,
    attachmentsRequired: true,
    buttonClass: "bg-indigo-600 hover:bg-indigo-700",
    gradientClass: "from-indigo-600 to-indigo-500",
    remarksPlaceholder: "State why reconsideration should override RTP (required)",
    submitLabel: "Submit Reconsider Override",
  },
};

const statusClassName = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "APPROVED") return "bg-emerald-100 text-emerald-700";
  if (normalized === "REJECTED") return "bg-rose-100 text-rose-700";
  if (normalized === "PENDING") return "bg-sky-100 text-sky-700";
  return "bg-amber-100 text-amber-700";
};

const formatRequestType = (value) => {
  const normalized = String(value || "").toUpperCase();
  if (normalized === "RECONSIDER_OVERRIDE") return "Reconsider Override";
  if (normalized === "MARK_NOT_VALID_RTP") return "Mark Not Valid For RTP";
  if (normalized === "MARK_VALID_RTP") return "Mark as Valid RTP";
  return "-";
};

const resolveErrorMessage = (error, fallback) => {
  const message = error?.response?.data?.message;
  if (!message) return fallback;
  if (typeof message === "string") return message;
  if (Array.isArray(message)) return message.join(", ");
  if (typeof message === "object") return Object.values(message).flat().join(", ");
  return fallback;
};

const getActionMeta = (actionType) => {
  const normalized = String(actionType || "").toUpperCase();
  return ENTRY_ACTION_TYPES[normalized] || ENTRY_ACTION_TYPES.MARK_NOT_VALID_RTP;
};

const formatBytes = (value) => {
  const bytes = Number(value || 0);
  if (bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const normalizeReasonCode = (value) => String(value || "").trim().toUpperCase();

const ClaimsRtpEntry = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [search, setSearch] = useState("");
  const [batchNo, setBatchNo] = useState("");
  const [approvalStatus, setApprovalStatus] = useState("FOR_REVIEW");
  const [rtpCodeFilter, setRtpCodeFilter] = useState("ALL");
  const [corporateName, setCorporateName] = useState("");
  const [transDateFrom, setTransDateFrom] = useState("");
  const [transDateTo, setTransDateTo] = useState("");
  const [receivedDateFrom, setReceivedDateFrom] = useState("");
  const [receivedDateTo, setReceivedDateTo] = useState("");
  const [reasonOptions, setReasonOptions] = useState([]);

  const [selectedRow, setSelectedRow] = useState(null);
  const [modalMode, setModalMode] = useState(""); // view | entry_action
  const [requestType, setRequestType] = useState("MARK_NOT_VALID_RTP");
  const [remarks, setRemarks] = useState("");
  const [rtpAttachments, setRtpAttachments] = useState([]);
  const [claimAttachments, setClaimAttachments] = useState([]);
  const [soaAttachments, setSoaAttachments] = useState([]);
  const [latestApprovalAttachments, setLatestApprovalAttachments] = useState([]);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkRequestType, setBulkRequestType] = useState("MARK_NOT_VALID_RTP");
  const [bulkRemarks, setBulkRemarks] = useState("");
  const [bulkFiles, setBulkFiles] = useState([]);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [downloadRows, setDownloadRows] = useState([]);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isViewDetailsMaximized, setIsViewDetailsMaximized] = useState(false);
  const [isExportingCsv, setIsExportingCsv] = useState(false);
  const fileInputRef = useRef(null);
  const bulkFileInputRef = useRef(null);

  const maxUploadLimit = 2;
  const allowedFileTypes = ["pdf", "jpg", "jpeg", "png"];
  const maxFileSizeMB = 10;
  const token = localStorage.getItem("authentication/token");

  const getRowKey = useCallback(
    (row) => `${String(row?.batch_no || "")}::${String(row?.trans_no || "")}`,
    [],
  );

  const isPendingApproval = useCallback(
    (row) => String(row?.latest_approval_status || "").toUpperCase() === "PENDING",
    [],
  );

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/claims/processing/rtp-entry`, {
        params: {
          page,
          per_page: perPage,
          search: search.trim(),
          batch_no: batchNo.trim(),
          approval_status: approvalStatus,
          rtp_code: rtpCodeFilter === "ALL" ? "" : rtpCodeFilter,
          corporate_name: corporateName.trim(),
          trans_date_from: transDateFrom || "",
          trans_date_to: transDateTo || "",
          received_date_from: receivedDateFrom || "",
          received_date_to: receivedDateTo || "",
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setRows(response.data?.data || []);
      const pagination = response.data?.pagination || {};
      setTotalPages(pagination.total_pages || 1);
      setTotalRecords(pagination.total || 0);
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to fetch For RTP entry rows."), {
        duration: 4000,
      });
      setRows([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    perPage,
    search,
    batchNo,
    approvalStatus,
    rtpCodeFilter,
    corporateName,
    transDateFrom,
    transDateTo,
    receivedDateFrom,
    receivedDateTo,
    token,
  ]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    let mounted = true;
    const fetchReasonOptions = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/claims/processing/rtp-entry/reasons`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!mounted) return;
        setReasonOptions(response.data?.data || []);
      } catch (error) {
        if (!mounted) return;
        setReasonOptions([]);
      }
    };

    fetchReasonOptions();
    return () => {
      mounted = false;
    };
  }, [token]);

  useEffect(() => {
    setSelectedRowKeys((prev) => {
      if (prev.length === 0) return prev;
      const currentKeys = new Set(rows.map((row) => getRowKey(row)));
      return prev.filter((key) => currentKeys.has(key));
    });
  }, [rows, getRowKey]);

  const showingText = useMemo(() => {
    if (!totalRecords) return "No records";
    const from = (page - 1) * perPage + 1;
    const to = Math.min(page * perPage, totalRecords);
    return `Showing ${from} to ${to} of ${totalRecords}`;
  }, [page, perPage, totalRecords]);

  const selectedBulkRows = useMemo(() => {
    if (selectedRowKeys.length === 0) return [];
    const keySet = new Set(selectedRowKeys);
    return rows.filter((row) => keySet.has(getRowKey(row)));
  }, [rows, selectedRowKeys, getRowKey]);

  const selectableRows = useMemo(
    () => rows.filter((row) => !isPendingApproval(row)),
    [rows, isPendingApproval],
  );

  const allSelectableSelected =
    selectableRows.length > 0 &&
    selectableRows.every((row) => selectedRowKeys.includes(getRowKey(row)));

  const fetchDetails = async (transNo) => {
    const response = await axios.get(`${BASE_URL}/claims/processing/rtp-entry/details`, {
      params: { trans_no: transNo },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data || {};
  };

  const openViewModal = async (row) => {
    const loadingToastId = toast.loading("Loading For RTP details...");
    try {
      const details = await fetchDetails(row.trans_no);
      setSelectedRow({ ...row, _details: details });
      setRtpAttachments(details?.attachments || []);
      setClaimAttachments(details?.claim_attachments || []);
      setSoaAttachments(details?.soa_attachments || []);
      setLatestApprovalAttachments(details?.latest_approval_attachments || []);
      setModalMode("view");
      toast.success("For RTP Entry details loaded.", { duration: 2000 });
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to load RTP details."), {
        duration: 4000,
      });
    } finally {
      toast.dismiss(loadingToastId);
    }
  };

  const openEntryActionModal = async (row, actionType) => {
    if (isPendingApproval(row)) {
      toast.error("This claim already has a pending final approval request.", {
        duration: 4000,
      });
      return;
    }

    try {
      const details = await fetchDetails(row.trans_no);
      setSelectedRow({ ...row, _details: details });
      setRequestType(actionType);
      setRemarks("");
      setRtpAttachments(details?.attachments || []);
      setClaimAttachments(details?.claim_attachments || []);
      setSoaAttachments(details?.soa_attachments || []);
      setLatestApprovalAttachments(details?.latest_approval_attachments || []);
      setUploadQueue([]);
      setModalMode("entry_action");
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to load RTP details."), {
        duration: 4000,
      });
    }
  };

  const closeModal = () => {
    setSelectedRow(null);
    setModalMode("");
    setRequestType("MARK_NOT_VALID_RTP");
    setRemarks("");
    setRtpAttachments([]);
    setClaimAttachments([]);
    setSoaAttachments([]);
    setLatestApprovalAttachments([]);
    setUploadQueue([]);
    setIsSubmitting(false);
    setIsViewDetailsMaximized(false);
  };

  const refreshFileLink = async (fileObj, scope = "rtp") => {
    if (!fileObj?.upload_id) {
      toast.error("Missing upload ID for refresh.", { duration: 3500 });
      return;
    }

    const loadingToastId = toast.loading("Refreshing file link...");
    try {
      const response = await axios.post(
        `${BASE_URL}/claims/refresh-url`,
        { upload_id: fileObj.upload_id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const refreshedUrl =
        response.data?.signed_url || response.data?.file_url || response.data?.file?.signed_url;
      if (!refreshedUrl) {
        toast.error("Failed to refresh file link.", { duration: 3500 });
        return;
      }

      const updateList = (prev) =>
        prev.map((item) =>
          item.upload_id === fileObj.upload_id ? { ...item, signed_url: refreshedUrl } : item,
        );

      if (scope === "claim") {
        setClaimAttachments(updateList);
      } else if (scope === "soa") {
        setSoaAttachments(updateList);
      } else if (scope === "approval") {
        setLatestApprovalAttachments(updateList);
      } else {
        setRtpAttachments(updateList);
      }

      toast.success("Link refreshed successfully.", { duration: 3000 });
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to refresh file link."), {
        duration: 4000,
      });
    } finally {
      toast.dismiss(loadingToastId);
    }
  };

  const chooseFile = () => {
    fileInputRef.current?.click();
  };

  const uploadAttachment = async (queueItem, transNo) => {
    setUploadQueue((prev) =>
      prev.map((item) => (item.file === queueItem.file ? { ...item, isUploading: true } : item)),
    );

    try {
      const form = new FormData();
      form.append("file", queueItem.file);
      form.append("trans_no", transNo);

      const response = await axios.post(`${BASE_URL}/claims/processing/rtp-entry/attach-files`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (event) => {
          const progress = Math.round((event.loaded * 100) / event.total);
          setUploadQueue((prev) =>
            prev.map((item) => (item.file === queueItem.file ? { ...item, progress } : item)),
          );
        },
      });

      const uploadedFile = response.data?.files?.[0];
      if (uploadedFile) {
        setRtpAttachments((prev) => [
          {
            upload_id: uploadedFile.upload_id,
            original_file_name: uploadedFile.original_file_name,
            signed_url: uploadedFile.signed_url,
            url_expires_at: uploadedFile.expires_at,
          },
          ...prev,
        ]);
      }

      setUploadQueue((prev) => prev.filter((item) => item.file !== queueItem.file));
    } catch (error) {
      const errorMessage = resolveErrorMessage(error, "Failed to upload attachment.");
      setUploadQueue((prev) =>
        prev.map((item) =>
          item.file === queueItem.file ? { ...item, isUploading: false, error: errorMessage } : item,
        ),
      );
      toast.error(errorMessage, { duration: 4000 });
    }
  };

  const validateFiles = (selectedFiles, occupiedSlots = 0) => {
    const validFiles = Array.from(selectedFiles).filter((file) => {
      const extension = String(file.name || "")
        .split(".")
        .pop()
        .toLowerCase();
      const allowedType = allowedFileTypes.includes(extension);
      const withinSize = file.size <= maxFileSizeMB * 1024 * 1024;

      if (!allowedType) {
        toast.error(`File ${file.name} is not supported.`, { duration: 4000 });
        return false;
      }
      if (!withinSize) {
        toast.error(`File ${file.name} exceeds ${maxFileSizeMB}MB size limit.`, { duration: 4000 });
        return false;
      }
      return true;
    });

    if (occupiedSlots + validFiles.length > maxUploadLimit) {
      toast.error(`You can only upload up to ${maxUploadLimit} files.`, { duration: 4000 });
      return [];
    }

    return validFiles;
  };

  const onFileChange = (selectedFiles) => {
    if (!selectedRow?.trans_no) return;

    const totalCount = rtpAttachments.length + uploadQueue.length;
    const validFiles = validateFiles(selectedFiles, totalCount);
    if (validFiles.length === 0) return;

    const queueItems = validFiles.map((file) => ({
      file,
      progress: 0,
      isUploading: false,
      error: null,
    }));

    setUploadQueue((prev) => [...prev, ...queueItems]);
    queueItems.forEach((queueItem) => uploadAttachment(queueItem, selectedRow.trans_no));
  };

  const removeAttachment = async (fileObj) => {
    if (!fileObj?.upload_id) return;
    try {
      await axios.delete(`${BASE_URL}/claims/processing/rtp-entry/attachments/${fileObj.upload_id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setRtpAttachments((prev) => prev.filter((item) => item.upload_id !== fileObj.upload_id));
      toast.success("Attachment removed.", { duration: 3000 });
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to remove attachment."), {
        duration: 4000,
      });
    }
  };

  const submitEntryActionRequest = async () => {
    if (!selectedRow) return;

    const meta = getActionMeta(requestType);
    if (meta.remarksRequired && !remarks.trim()) {
      toast.error(`Remarks are required for ${meta.label}.`, { duration: 4000 });
      return;
    }

    if (meta.attachmentsRequired && rtpAttachments.length === 0) {
      toast.error(`${meta.label} requires at least one reconsideration letter attachment.`, {
        duration: 4000,
      });
      return;
    }

    try {
      setIsSubmitting(true);

      await axios.post(
        meta.endpoint,
        {
          batch_no: selectedRow.batch_no,
          trans_no: selectedRow.trans_no,
          loa_no: selectedRow.loa_no,
          action_type: requestType,
          remarks: remarks.trim(),
          attachment_upload_ids: rtpAttachments.map((item) => item.upload_id).filter(Boolean),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      toast.success("Submitted to Final For RTP Approval Queue.", { duration: 3000 });
      closeModal();
      fetchRows();
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to submit request."), {
        duration: 4000,
      });
      setIsSubmitting(false);
    }
  };

  const clearBulkSelection = () => {
    setSelectedRowKeys([]);
  };

  const toggleRowSelection = (row) => {
    if (isPendingApproval(row)) return;
    const key = getRowKey(row);
    setSelectedRowKeys((prev) => {
      if (prev.includes(key)) {
        return prev.filter((item) => item !== key);
      }
      return [...prev, key];
    });
  };

  const toggleSelectAllCurrentPage = () => {
    if (selectableRows.length === 0) return;
    if (allSelectableSelected) {
      setSelectedRowKeys([]);
      return;
    }
    setSelectedRowKeys(selectableRows.map((row) => getRowKey(row)));
  };

  const closeBulkActionModal = () => {
    setIsBulkModalOpen(false);
    setBulkRequestType("MARK_NOT_VALID_RTP");
    setBulkRemarks("");
    setBulkFiles([]);
    setIsBulkSubmitting(false);
  };

  const openBulkActionModal = (actionType) => {
    if (selectedBulkRows.length === 0) {
      toast.error("Select at least one row before using bulk action.", { duration: 4000 });
      return;
    }

    const pendingRows = selectedBulkRows.filter((row) => isPendingApproval(row));
    if (pendingRows.length > 0) {
      toast.error("Some selected rows are already pending final approval.", { duration: 4000 });
      return;
    }

    setBulkRequestType(actionType);
    setBulkRemarks("");
    setBulkFiles([]);
    setIsBulkModalOpen(true);
  };

  const chooseBulkFile = () => {
    bulkFileInputRef.current?.click();
  };

  const onBulkFileChange = (selectedFiles) => {
    const validFiles = validateFiles(selectedFiles, bulkFiles.length);
    if (validFiles.length === 0) return;
    setBulkFiles((prev) => [...prev, ...validFiles]);
  };

  const removeBulkFile = (index) => {
    setBulkFiles((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  };

  const uploadBulkAttachmentForTransaction = async (file, transNo) => {
    const form = new FormData();
    form.append("file", file);
    form.append("trans_no", transNo);

    const response = await axios.post(`${BASE_URL}/claims/processing/rtp-entry/attach-files`, form, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    });

    const uploaded = response?.data?.files?.[0];
    if (!uploaded?.upload_id) {
      throw new Error("Attachment upload succeeded but no upload_id was returned.");
    }
    return uploaded.upload_id;
  };

  const submitBulkEntryActionRequest = async () => {
    const meta = getActionMeta(bulkRequestType);
    if (selectedBulkRows.length === 0) {
      toast.error("No selected rows found for bulk submission.", { duration: 4000 });
      return;
    }

    if (meta.remarksRequired && !bulkRemarks.trim()) {
      toast.error(`Remarks are required for ${meta.label}.`, { duration: 4000 });
      return;
    }

    if (meta.attachmentsRequired && bulkFiles.length === 0) {
      toast.error(`${meta.label} requires at least one reconsideration letter attachment.`, {
        duration: 4000,
      });
      return;
    }

    try {
      setIsBulkSubmitting(true);
      let successCount = 0;
      const failedRows = [];

      for (const row of selectedBulkRows) {
        try {
          if (isPendingApproval(row)) {
            throw new Error("Already pending final approval.");
          }

          const uploadedIds = [];
          for (const file of bulkFiles) {
            const uploadId = await uploadBulkAttachmentForTransaction(file, row.trans_no);
            uploadedIds.push(uploadId);
          }

          await axios.post(
            meta.endpoint,
            {
              batch_no: row.batch_no,
              trans_no: row.trans_no,
              loa_no: row.loa_no,
              action_type: bulkRequestType,
              remarks: bulkRemarks.trim(),
              attachment_upload_ids: uploadedIds,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          successCount += 1;
        } catch (error) {
          failedRows.push(
            `TRN ${row.trans_no || "-"}: ${resolveErrorMessage(error, "Request failed")}`,
          );
        }
      }

      if (successCount > 0) {
        toast.success(
          `${successCount} row(s) submitted to Final For RTP Approval Queue.`,
          { duration: 3500 },
        );
      }

      if (failedRows.length > 0) {
        toast.error(
          `${failedRows.length} row(s) failed. ${failedRows.slice(0, 2).join(" | ")}`,
          { duration: 6000 },
        );
      }

      if (successCount > 0) {
        closeBulkActionModal();
        clearBulkSelection();
        fetchRows();
      } else {
        setIsBulkSubmitting(false);
      }
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Bulk submission failed."), { duration: 4000 });
      setIsBulkSubmitting(false);
    }
  };

  const getDownloadContext = (rowsForValidation) => {
    const rowsList = rowsForValidation || [];
    if (rowsList.length === 0) {
      return { ok: false, message: "Select at least one row to download RTP Form." };
    }

    const providerCodes = Array.from(
      new Set(rowsList.map((row) => String(row.provider_code || "").toUpperCase().trim()).filter(Boolean)),
    );
    if (providerCodes.length > 1) {
      return { ok: false, message: "Download RTP Form only allows the same provider." };
    }

    const batchNos = Array.from(
      new Set(rowsList.map((row) => String(row.batch_no || "").toUpperCase().trim()).filter(Boolean)),
    );
    if (batchNos.length > 1) {
      return { ok: false, message: "Download RTP Form only allows the same batch number." };
    }

    const reasonCodes = rowsList.map((row) => normalizeReasonCode(row.rtp_code));
    if (reasonCodes.some((code) => !code)) {
      return { ok: false, message: "Some selected rows have no RTP reason code." };
    }

    const uniqueReasonCodes = Array.from(new Set(reasonCodes));
    if (uniqueReasonCodes.length > 1) {
      return { ok: false, message: "Download RTP Form only allows the same RTP reason." };
    }

    return {
      ok: true,
      providerCode: providerCodes[0] || "",
      batchNo: batchNos[0] || "",
      reasonCode: uniqueReasonCodes[0] || "",
    };
  };

  const openDownloadModal = () => {
    const context = getDownloadContext(selectedBulkRows);
    if (!context.ok) {
      toast.error(context.message, { duration: 4500 });
      return;
    }

    setDownloadRows(selectedBulkRows);
    setIsDownloadModalOpen(true);
  };

  const closeDownloadModal = () => {
    setIsDownloadModalOpen(false);
    setDownloadRows([]);
    setIsGeneratingPdf(false);
  };

  const submitDownloadFormPdf = async () => {
    const context = getDownloadContext(downloadRows);
    if (!context.ok) {
      toast.error(context.message, { duration: 4500 });
      return;
    }

    try {
      setIsGeneratingPdf(true);
      const response = await axios.post(
        `${BASE_URL}/claims/processing/rtp-entry/download-form`,
        {
          trans_nos: downloadRows.map((row) => String(row.trans_no || "")).filter(Boolean),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const signedUrl = response?.data?.data?.signed_url;
      if (!signedUrl) {
        throw new Error("No downloadable file URL was returned.");
      }

      window.open(signedUrl, "_blank", "noopener,noreferrer");
      toast.success("RTP Form generated. Download tab opened.", { duration: 3500 });
      closeDownloadModal();
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to generate RTP Form PDF."), {
        duration: 4500,
      });
      setIsGeneratingPdf(false);
    }
  };

  const exportRtpEntryCsv = async () => {
    try {
      setIsExportingCsv(true);
      const response = await axios.get(`${BASE_URL}/claims/processing/rtp-entry/export`, {
        params: {
          approval_status: approvalStatus,
          batch_no: batchNo.trim(),
          rtp_code: rtpCodeFilter === "ALL" ? "" : rtpCodeFilter,
          corporate_name: corporateName.trim(),
          trans_date_from: transDateFrom || "",
          trans_date_to: transDateTo || "",
          received_date_from: receivedDateFrom || "",
          received_date_to: receivedDateTo || "",
          search: search.trim(),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `claims-rtp-entry-${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("For RTP Entry CSV export generated.", { duration: 3000 });
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to export For RTP Entry CSV."), {
        duration: 4000,
      });
    } finally {
      setIsExportingCsv(false);
    }
  };

  const applyFilters = () => {
    if (transDateFrom && transDateTo && transDateFrom > transDateTo) {
      toast.error("Transaction date range is invalid.", { duration: 3500 });
      return;
    }
    if (receivedDateFrom && receivedDateTo && receivedDateFrom > receivedDateTo) {
      toast.error("Received date range is invalid.", { duration: 3500 });
      return;
    }
    setPage(1);
    fetchRows();
  };

  const resetFilters = () => {
    setSearch("");
    setBatchNo("");
    setApprovalStatus("FOR_REVIEW");
    setRtpCodeFilter("ALL");
    setCorporateName("");
    setTransDateFrom("");
    setTransDateTo("");
    setReceivedDateFrom("");
    setReceivedDateTo("");
    setPage(1);
    clearBulkSelection();
  };

  const bulkMeta = getActionMeta(bulkRequestType);
  const downloadContext = getDownloadContext(downloadRows);

  const renderFileList = (title, files, scope) => (
    <div className="rounded-lg border border-gray-100 p-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</div>
      {files.length > 0 ? (
        <ul className="space-y-2 text-xs">
          {files.map((file) => (
            <li
              key={`${scope}-${file.upload_id || file.original_file_name}`}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2"
            >
              <div className="min-w-0">
                <a
                  href={file.signed_url}
                  target="_blank"
                  rel="noreferrer"
                  className="block truncate text-blue-600 hover:underline"
                >
                  {file.original_file_name || "Untitled file"}
                </a>
                <div className="text-[11px] text-slate-500">
                  {formatBytes(file.file_size)} {file.url_expires_at ? `| Expires: ${formatDate(file.url_expires_at)}` : ""}
                </div>
              </div>
              {file.upload_id ? (
                <button
                  type="button"
                  onClick={() => refreshFileLink(file, scope)}
                  className="rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-100"
                >
                  <FontAwesomeIcon icon={faSyncAlt} className="mr-1" />
                  Refresh Link
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-sm text-gray-500">No file found.</div>
      )}
    </div>
  );

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">For RTP Entry Module</h1>
          <p className="text-sm text-gray-600">
            Initial specialist review for auto-detected For RTP claims from batching.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={exportRtpEntryCsv}
            disabled={isExportingCsv}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FontAwesomeIcon icon={isExportingCsv ? faSpinner : faFileExport} spin={isExportingCsv} className="mr-2" />
            {isExportingCsv ? "Exporting..." : "Export CSV"}
          </button>
          <button
            onClick={fetchRows}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50"
          >
            <FontAwesomeIcon icon={faSyncAlt} className="mr-2" />
            Refresh
          </button>
        </div>
      </div>

      <div className="mb-4 rounded-xl border border-gray-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <div>
            <label className="text-xs text-gray-600">Approval Status</label>
            <select
              value={approvalStatus}
              onChange={(e) => setApprovalStatus(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              {APPROVAL_FILTERS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-600">Batch #</label>
            <input
              value={batchNo}
              onChange={(e) => setBatchNo(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              placeholder="Batch no"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Search</label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              placeholder="LOA / TRN / Provider / Member"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">RTP Reason</label>
            <select
              value={rtpCodeFilter}
              onChange={(e) => setRtpCodeFilter(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              <option value="ALL">All RTP Reasons</option>
              {reasonOptions.map((reason) => (
                <option key={reason.rtp_code} value={reason.rtp_code}>
                  {reason.reasons} ({reason.rtp_code})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-600">Corporate Name</label>
            <input
              value={corporateName}
              onChange={(e) => setCorporateName(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              placeholder="Corporate"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Transaction Date From</label>
            <input
              type="date"
              value={transDateFrom}
              onChange={(e) => setTransDateFrom(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Transaction Date To</label>
            <input
              type="date"
              value={transDateTo}
              onChange={(e) => setTransDateTo(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Date Received From</label>
            <input
              type="date"
              value={receivedDateFrom}
              onChange={(e) => setReceivedDateFrom(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Date Received To</label>
            <input
              type="date"
              value={receivedDateTo}
              onChange={(e) => setReceivedDateTo(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Per Page</label>
            <select
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              {[10, 20, 50, 100, 200, 500, 1000, 1500].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <button onClick={applyFilters} className="rounded-lg bg-gray-900 px-3 py-2 text-sm text-white">
            Apply Filters
          </button>
          <button onClick={resetFilters} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm">
            Reset
          </button>
        </div>
      </div>

      <div className="mb-4 overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-50 via-white to-slate-50 p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Bulk Entry Actions</div>
            <div className="text-sm text-slate-700">
              Selected rows: <span className="font-semibold text-slate-900">{selectedBulkRows.length}</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => openBulkActionModal("MARK_VALID_RTP")}
              disabled={selectedBulkRows.length === 0 || isBulkSubmitting}
              className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
              Bulk Mark as Valid RTP
            </button>
            <button
              onClick={() => openBulkActionModal("MARK_NOT_VALID_RTP")}
              disabled={selectedBulkRows.length === 0 || isBulkSubmitting}
              className="rounded-lg bg-rose-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faTimesCircle} className="mr-1" />
              Bulk Mark Not Valid RTP
            </button>
            <button
              onClick={() => openBulkActionModal("RECONSIDER_OVERRIDE")}
              disabled={selectedBulkRows.length === 0 || isBulkSubmitting}
              className="rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
              Bulk Reconsider Override
            </button>
            <button
              onClick={openDownloadModal}
              disabled={selectedBulkRows.length === 0 || isBulkSubmitting}
              className="rounded-lg bg-slate-800 px-3 py-2 text-xs font-semibold text-white transition hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faFilePdf} className="mr-1" />
              Download RTP Form
            </button>
            <button
              onClick={clearBulkSelection}
              disabled={selectedBulkRows.length === 0}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Clear Selection
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="flex items-center justify-between p-3 text-sm text-gray-600">
          <span>{showingText}</span>
          <span>Initial RTP Review Queue</span>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="p-3 text-left">
                  <input
                    type="checkbox"
                    checked={allSelectableSelected}
                    onChange={toggleSelectAllCurrentPage}
                    title="Select all non-pending rows in this page"
                  />
                </th>
                <th className="p-3 text-left">Batch #</th>
                <th className="p-3 text-left">Transaction #</th>
                <th className="p-3 text-left">LOA #</th>
                <th className="p-3 text-left">Provider</th>
                <th className="p-3 text-left">Corporate</th>
                <th className="p-3 text-left">Member</th>
                <th className="p-3 text-left">Transaction Date</th>
                <th className="p-3 text-left">Date Received</th>
                <th className="p-3 text-left">Coverage</th>
                <th className="p-3 text-left">Claim Status</th>
                <th className="p-3 text-left">Stale</th>
                <th className="p-3 text-left">RTP Code</th>
                <th className="p-3 text-left">RTP Reason</th>
                <th className="p-3 text-left">Latest Entry Action</th>
                <th className="p-3 text-left">Approval Status</th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan="17" className="p-5 text-center text-gray-500">
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Loading For RTP entries...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="17" className="p-5 text-center text-gray-500">
                    No For RTP entry rows found.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const approval = String(row.latest_approval_status || "").toUpperCase();
                  const approvalLabel = approval || "FOR REVIEW";
                  const rowKey = getRowKey(row);
                  const isSelected = selectedRowKeys.includes(rowKey);
                  const pending = isPendingApproval(row);
                  return (
                    <tr key={`${row.batch_no}-${row.trans_no}`}>
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={pending}
                          onChange={() => toggleRowSelection(row)}
                          title={pending ? "Pending final approval" : "Select row for bulk action"}
                        />
                      </td>
                      <td className="p-3">{row.batch_no || "-"}</td>
                      <td className="p-3">{row.trans_no || "-"}</td>
                      <td className="p-3">{row.loa_no || "-"}</td>
                      <td className="p-3">{row.provider_name || row.provider_code || "-"}</td>
                      <td className="p-3">{row.corporate_name || "-"}</td>
                      <td className="p-3">{row.member_name || "-"}</td>
                      <td className="p-3">{formatDate(row.trans_date)}</td>
                      <td className="p-3">{formatDate(row.received_date)}</td>
                      <td className="p-3">{row.coverage_code || "-"}</td>
                      <td className="p-3">{row.claim_status || "-"}</td>
                      <td className="p-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                            Number(row.stale) === 1
                              ? "bg-rose-100 text-rose-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {Number(row.stale) === 1 ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className="p-3">{row.rtp_code || "-"}</td>
                      <td className="p-3">{row.rtp_reason || "-"}</td>
                      <td className="p-3">{formatRequestType(row.latest_approval_reason)}</td>
                      <td className="p-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusClassName(
                            approvalLabel,
                          )}`}
                        >
                          {approvalLabel}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => openViewModal(row)}
                            className="rounded-lg bg-slate-600 px-2 py-1 text-xs text-white hover:bg-slate-700"
                          >
                            <FontAwesomeIcon icon={faEye} className="mr-1" />
                            View
                          </button>
                          <button
                            onClick={() => openEntryActionModal(row, "MARK_VALID_RTP")}
                            disabled={approval === "PENDING"}
                            className={`rounded-lg px-2 py-1 text-xs text-white ${
                              approval === "PENDING"
                                ? "cursor-not-allowed bg-sky-400"
                                : "bg-emerald-600 hover:bg-emerald-700"
                            }`}
                          >
                            <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                            {approval === "PENDING" ? "Pending Final Approval" : "Mark as Valid RTP"}
                          </button>
                          <button
                            onClick={() => openEntryActionModal(row, "MARK_NOT_VALID_RTP")}
                            disabled={approval === "PENDING"}
                            className={`rounded-lg px-2 py-1 text-xs text-white ${
                              approval === "PENDING"
                                ? "cursor-not-allowed bg-sky-400"
                                : "bg-rose-600 hover:bg-rose-700"
                            }`}
                          >
                            <FontAwesomeIcon icon={faTimesCircle} className="mr-1" />
                            {approval === "PENDING" ? "Pending Final Approval" : "Mark Not Valid RTP"}
                          </button>
                          <button
                            onClick={() => openEntryActionModal(row, "RECONSIDER_OVERRIDE")}
                            disabled={approval === "PENDING"}
                            className={`rounded-lg px-2 py-1 text-xs text-white ${
                              approval === "PENDING"
                                ? "cursor-not-allowed bg-sky-400"
                                : "bg-indigo-600 hover:bg-indigo-700"
                            }`}
                          >
                            <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                            {approval === "PENDING" ? "Pending Final Approval" : "Reconsider Override"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between p-3 text-sm text-gray-600">
          <button
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={page === 1}
            className="rounded-md border border-gray-200 px-3 py-1 disabled:opacity-50"
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={page >= totalPages}
            className="rounded-md border border-gray-200 px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {selectedRow && modalMode === "view" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            className={`w-full rounded-xl bg-white p-6 shadow-lg transition-all ${
              isViewDetailsMaximized
                ? "max-w-[95vw] h-[92vh] overflow-y-auto"
                : "max-w-3xl max-h-[90vh] overflow-y-auto"
            }`}
          >
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">For RTP Entry Details</h2>
                <p className="text-xs text-gray-500">
                  Batch #{selectedRow.batch_no} | TRN #{selectedRow.trans_no}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsViewDetailsMaximized((prev) => !prev)}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                >
                  <FontAwesomeIcon icon={isViewDetailsMaximized ? faCompress : faExpand} className="mr-1" />
                  {isViewDetailsMaximized ? "Original Size" : "Maximize"}
                </button>
                <button
                  onClick={closeModal}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="rounded-lg border border-gray-100 p-3">
                <div className="text-xs text-gray-500">LOA #</div>
                <div>{selectedRow.loa_no || "-"}</div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <div className="text-xs text-gray-500">Provider</div>
                <div>{selectedRow.provider_name || selectedRow.provider_code || "-"}</div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <div className="text-xs text-gray-500">Corporate</div>
                <div>{selectedRow.corporate_name || selectedRow?._details?.transaction?.corporate_name || "-"}</div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <div className="text-xs text-gray-500">Transaction Date</div>
                <div>{formatDate(selectedRow.trans_date || selectedRow?._details?.transaction?.trans_date)}</div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <div className="text-xs text-gray-500">Date Received</div>
                <div>{formatDate(selectedRow.received_date || selectedRow?._details?.transaction?.received_date)}</div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <div className="text-xs text-gray-500">Claim Status</div>
                <div>{selectedRow.claim_status || selectedRow?._details?.transaction?.claim_status || "-"}</div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3 md:col-span-3">
                <div className="text-xs text-gray-500">RTP Reason / Code</div>
                <div>
                  {(selectedRow.rtp_reason || selectedRow?._details?.rtp?.reasons || "-")} /{" "}
                  {selectedRow.rtp_code || selectedRow?._details?.rtp?.rtp_code || "-"}
                </div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3 md:col-span-3">
                <div className="text-xs text-gray-500">RTP Remarks</div>
                <div>{selectedRow?._details?.rtp?.remarks || selectedRow.rtp_remarks || "-"}</div>
              </div>
              <div className="space-y-3 md:col-span-3">
                {renderFileList("RTP Entry Attachments", rtpAttachments, "rtp")}
                {renderFileList("Uploaded LOA / Claim Attachments", claimAttachments, "claim")}
                {renderFileList("Uploaded SOA Attachments", soaAttachments, "soa")}
                {renderFileList(
                  "Latest Approval Attachments",
                  latestApprovalAttachments,
                  "approval",
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedRow && modalMode === "entry_action" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-lg">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{getActionMeta(requestType).label}</h2>
                <p className="text-xs text-gray-500">
                  This request will be forwarded to Final For RTP Approval Queue.
                </p>
              </div>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600">Remarks</label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  placeholder={getActionMeta(requestType).remarksPlaceholder}
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">
                  Attachments
                  {getActionMeta(requestType).attachmentsRequired
                    ? ` (required reconsideration letter, max ${maxUploadLimit})`
                    : ` (optional, max ${maxUploadLimit})`}
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={chooseFile}
                    className="rounded-lg bg-slate-100 px-3 py-2 text-xs hover:bg-slate-200"
                  >
                    <FontAwesomeIcon icon={faPaperclip} className="mr-1" />
                    Upload
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => onFileChange(e.target.files)}
                  />
                </div>

                {uploadQueue.length > 0 && (
                  <div className="mt-3 space-y-2 text-xs">
                    {uploadQueue.map((item) => (
                      <div key={item.file.name} className="rounded-lg bg-slate-50 px-3 py-2">
                        <div className="flex items-center justify-between">
                          <span className="truncate">{item.file.name}</span>
                          {item.isUploading && <FontAwesomeIcon icon={faSpinner} spin />}
                        </div>
                        <div className="mt-2 h-2 rounded-full bg-slate-200">
                          <div className="h-2 rounded-full bg-emerald-500" style={{ width: `${item.progress}%` }} />
                        </div>
                        {item.error && <p className="mt-1 text-rose-600">{item.error}</p>}
                      </div>
                    ))}
                  </div>
                )}

                {rtpAttachments.length > 0 && (
                  <ul className="mt-3 space-y-2 text-xs">
                    {rtpAttachments.map((file) => (
                      <li
                        key={file.upload_id}
                        className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2"
                      >
                        <a
                          href={file.signed_url}
                          target="_blank"
                          rel="noreferrer"
                          className="truncate text-blue-600 hover:underline"
                        >
                          {file.original_file_name}
                        </a>
                        <button
                          type="button"
                          onClick={() => removeAttachment(file)}
                          className="text-rose-600 hover:text-rose-700"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={closeModal}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={submitEntryActionRequest}
                disabled={isSubmitting}
                className={`rounded-lg px-4 py-2 text-sm text-white disabled:opacity-50 ${
                  getActionMeta(requestType).buttonClass
                }`}
              >
                {isSubmitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                    {getActionMeta(requestType).submitLabel}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {isDownloadModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 p-5 text-white">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-white/80">
                    RTP Form PDF
                  </div>
                  <h3 className="mt-1 text-xl font-semibold">Download Consolidated RTP Form</h3>
                  <p className="mt-1 text-sm text-white/90">
                    Only rows with the same provider, batch number, and RTP reason can be generated together.
                  </p>
                </div>
                <button
                  onClick={closeDownloadModal}
                  className="rounded-lg border border-white/40 bg-white/10 px-3 py-2 text-xs font-semibold text-white transition hover:bg-white/20"
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-1" />
                  Close
                </button>
              </div>
            </div>

            <div className="space-y-4 p-5">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Selected Rows</div>
                  <div className="mt-1 text-lg font-semibold text-slate-900">{downloadRows.length}</div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Provider</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {downloadRows[0]?.provider_name || downloadRows[0]?.provider_code || "-"}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Batch</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {downloadRows[0]?.batch_no || "-"}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">RTP Reason</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {downloadRows[0]?.rtp_reason || "-"} ({downloadRows[0]?.rtp_code || "-"})
                  </div>
                </div>
              </div>

              {!downloadContext.ok && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {downloadContext.message}
                </div>
              )}

              <div className="overflow-hidden rounded-xl border border-slate-200">
                <div className="bg-slate-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Selected Claims for RTP Form
                </div>
                <div className="max-h-64 overflow-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-white text-slate-600">
                      <tr>
                        <th className="p-2 text-left">Batch #</th>
                        <th className="p-2 text-left">TRN #</th>
                        <th className="p-2 text-left">LOA #</th>
                        <th className="p-2 text-left">Provider</th>
                        <th className="p-2 text-left">Corporate</th>
                        <th className="p-2 text-left">Transaction Date</th>
                        <th className="p-2 text-left">Date Received</th>
                        <th className="p-2 text-left">RTP Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {downloadRows.map((row) => (
                        <tr key={`download-${getRowKey(row)}`}>
                          <td className="p-2">{row.batch_no || "-"}</td>
                          <td className="p-2">{row.trans_no || "-"}</td>
                          <td className="p-2">{row.loa_no || "-"}</td>
                          <td className="p-2">{row.provider_name || row.provider_code || "-"}</td>
                          <td className="p-2">{row.corporate_name || "-"}</td>
                          <td className="p-2">{formatDate(row.trans_date)}</td>
                          <td className="p-2">{formatDate(row.received_date)}</td>
                          <td className="p-2">
                            {row.rtp_reason || "-"} ({row.rtp_code || "-"})
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4">
              <button
                onClick={closeDownloadModal}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={submitDownloadFormPdf}
                disabled={isGeneratingPdf || !downloadContext.ok}
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isGeneratingPdf ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faDownload} className="mr-2" />
                    Generate & Download PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {isBulkModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className={`bg-gradient-to-r ${bulkMeta.gradientClass} p-5 text-white`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-white/80">
                    For RTP Entry Bulk Action
                  </div>
                  <h3 className="mt-1 text-xl font-semibold">{bulkMeta.label}</h3>
                  <p className="mt-1 text-sm text-white/90">
                    Selected rows will be submitted to Final For RTP Approval Queue with one shared request setup.
                  </p>
                </div>
                <button
                  onClick={closeBulkActionModal}
                  className="rounded-lg border border-white/40 bg-white/10 px-3 py-2 text-xs font-semibold text-white transition hover:bg-white/20"
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-1" />
                  Close
                </button>
              </div>
            </div>

            <div className="space-y-4 p-5">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Selected Rows</div>
                  <div className="mt-1 text-lg font-semibold text-slate-900">{selectedBulkRows.length}</div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Request Type</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{bulkMeta.label}</div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Attachments Rule</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {bulkMeta.attachmentsRequired ? "At least one required" : "Optional"}
                  </div>
                </div>
              </div>

              <div className="overflow-hidden rounded-xl border border-slate-200">
                <div className="bg-slate-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Selected Claims for Bulk Request
                </div>
                <div className="max-h-56 overflow-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-white text-slate-600">
                      <tr>
                        <th className="p-2 text-left">Batch #</th>
                        <th className="p-2 text-left">Transaction #</th>
                        <th className="p-2 text-left">LOA #</th>
                        <th className="p-2 text-left">Provider</th>
                        <th className="p-2 text-left">Claim Status</th>
                        <th className="p-2 text-left">Approval Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {selectedBulkRows.map((row) => (
                        <tr key={`bulk-${getRowKey(row)}`}>
                          <td className="p-2">{row.batch_no || "-"}</td>
                          <td className="p-2">{row.trans_no || "-"}</td>
                          <td className="p-2">{row.loa_no || "-"}</td>
                          <td className="p-2">{row.provider_name || row.provider_code || "-"}</td>
                          <td className="p-2">{row.claim_status || "-"}</td>
                          <td className="p-2">
                            <span
                              className={`inline-flex rounded-full px-2 py-1 text-[10px] font-semibold ${statusClassName(
                                row.latest_approval_status || "FOR_REVIEW",
                              )}`}
                            >
                              {String(row.latest_approval_status || "FOR_REVIEW").toUpperCase()}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">
                  Remarks {bulkMeta.remarksRequired ? "(required)" : "(optional)"}
                </label>
                <textarea
                  value={bulkRemarks}
                  onChange={(e) => setBulkRemarks(e.target.value)}
                  rows={3}
                  placeholder={bulkMeta.remarksPlaceholder}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">
                  Attachments {bulkMeta.attachmentsRequired ? "(required)" : "(optional)"} (max {maxUploadLimit})
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={chooseBulkFile}
                    className="rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-200"
                  >
                    <FontAwesomeIcon icon={faPaperclip} className="mr-1" />
                    Add Files
                  </button>
                  <input
                    ref={bulkFileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => onBulkFileChange(e.target.files)}
                  />
                  <span className="text-xs text-slate-500">
                    Allowed: PDF, JPG, JPEG, PNG up to {maxFileSizeMB}MB each.
                  </span>
                </div>

                {bulkFiles.length > 0 && (
                  <ul className="mt-3 space-y-2">
                    {bulkFiles.map((file, index) => (
                      <li
                        key={`${file.name}-${index}`}
                        className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs"
                      >
                        <div className="min-w-0">
                          <p className="truncate font-medium text-slate-700">{file.name}</p>
                          <p className="text-slate-500">{formatBytes(file.size)}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeBulkFile(index)}
                          className="text-rose-600 hover:text-rose-700"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4">
              <button
                onClick={closeBulkActionModal}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={submitBulkEntryActionRequest}
                disabled={isBulkSubmitting}
                className={`rounded-lg px-4 py-2 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50 ${bulkMeta.buttonClass}`}
              >
                {isBulkSubmitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Submitting Bulk Request...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                    Submit {selectedBulkRows.length} Request(s)
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimsRtpEntry;
